import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronDown, Menu, X } from "lucide-react";
import sseLogo from "@/assets/sse-logo-white-360.webp.asset.json";

type NavGroup = {
  label: string;
  links: { label: string; href: string }[];
};

/** Navigation mirrors the original site's three dropdown groups (see url-map.csv). */
const NAV_GROUPS: NavGroup[] = [
  {
    label: "About & Info",
    links: [
      { label: "About", href: "/about" },
      { label: "Team", href: "/team" },
      { label: "Case Studies", href: "/case-studies" },
      { label: "Blog", href: "/blog" },
      
      { label: "Engineering Good", href: "/engineered-good" },
      { label: "Contact", href: "/contact" },
    ],
  },
  {
    label: "Construction Services",
    links: [
      { label: "Swimming Pool Design", href: "/pools" },
      { label: "Screen Enclosures & Custom Aluminum", href: "/screen-enclosures" },
      { label: "Windows, Doors, Roofing & Exteriors", href: "/windows-doors-roofing-exteriors" },
      { label: "New Construction & Custom Structural", href: "/new-construction-and-custom-structural" },
      { label: "Verification Letters & Inspections", href: "/verification-letters-inspections" },
      { label: "Roof Letters", href: "/roof-letters" },
      { label: "Product Certification Consulting", href: "/product-certification-consulting" },
    ],
  },
  {
    label: "Renewables & Resiliency",
    links: [
      { label: "Solar Electric (PV) Design", href: "/pv" },
      { label: "Solar Thermal Design", href: "/solar-thermal-system-design" },
      { label: "Energy Storage Design", href: "/energy-storage" },
      { label: "Commercial Solar EPC Services", href: "/full-service-engineering-procurement-and-construction-epc" },
      { label: "Sales Training", href: "/sales-training" },
      { label: "SUNSYNC™", href: "/sunsync" },
    ],
  },
];

export function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMobileOpen(false);
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-border/40 bg-background/60 backdrop-blur">
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" className="flex items-center" onClick={() => setMobileOpen(false)}>
          <img src={sseLogo.url} alt="SunSmart Engineering" width={180} height={36} decoding="async" className="h-9 w-auto" />
        </Link>

        {/* Desktop dropdown nav */}
        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="group relative">
              <button
                type="button"
                className="flex items-center gap-1.5 py-2 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-foreground/85 transition-colors group-hover:text-sun"
              >
                {group.label}
                <ChevronDown className="h-3 w-3 transition-transform group-hover:rotate-180" />
              </button>
              <div className="invisible absolute left-1/2 top-full min-w-[280px] -translate-x-1/2 pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                <div className="rounded-sm border border-border bg-background/95 p-2 backdrop-blur-xl">
                  {group.links.map((l) => (
                    <Link
                      key={l.href + l.label}
                      to={l.href}
                      className="block px-4 py-2.5 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-foreground/75 transition-colors hover:bg-border/30 hover:text-sun"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-sun px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-ink hover:scale-[1.03] transition-transform"
          >
            Get started
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            className="inline-flex items-center justify-center rounded-full border border-border p-2.5 text-foreground/85 transition-colors hover:text-sun lg:hidden"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="max-h-[calc(100vh-73px)] overflow-y-auto border-t border-border/40 bg-background/95 backdrop-blur-xl lg:hidden">
          <nav className="mx-auto max-w-[1400px] px-6 py-6">
            {NAV_GROUPS.map((group) => (
              <div key={group.label} className="mb-6 last:mb-0">
                <div className="mb-2 flex items-center gap-4 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-sun">
                  {group.label}
                  <span className="h-px flex-1 bg-border" />
                </div>
                {group.links.map((l) => (
                  <Link
                    key={l.href + l.label}
                    to={l.href}
                    onClick={() => setMobileOpen(false)}
                    className="block py-2.5 font-display text-lg font-bold text-foreground/85 transition-colors hover:text-sun"
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            ))}
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-sun px-6 py-3 text-xs font-semibold uppercase tracking-wider text-ink"
            >
              Get started
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
